import type { MouseEvent, ReactNode } from 'react'
import { BiShapeTriangle } from 'react-icons/bi'
import TaActionButton from '../TaActionButton'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { runTrace } from '../../services/QuestionActions'
import { ICON_SM } from '../../common/theme'
import type { Question } from '../../types'
import { hasAnswerText } from '../../common/common'

interface GenerateTraceButtonProps {
  question: Question
  // Override the default triangle icon (e.g. row contexts that want a
  // MiniProgressDot rendering the sim score). The TaActionButton spinner
  // overlay still kicks in when `running` is true.
  icon?: ReactNode
  // Extra disabled on top of `!hasAnswer`. Lets callers gate by row state.
  disabled?: boolean
  iconSize?: number
  sx?: any
  testId?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

// Toolbar button that fires the per-question trace action. Owns the
// whole click → runTrace → result-toast flow internally so callers just
// drop it next to a question. Spins from the shared TaskManagerService
// processing registry that runTrace flips. Optional overrides let row
// contexts (e.g. BidQuestionsLeftPanel) customise the icon, sizing and
// hover handlers without re-implementing the click pipeline.
export default function GenerateTraceButton({
  question, icon, disabled, iconSize, sx, testId, onMouseEnter, onMouseLeave,
}: GenerateTraceButtonProps) {
  const { updateQuestion, getCurrentProject } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    const r = await runTrace(question, project, updateQuestion)
    if (r.success) addToast('success', 'Trace generated')
    else addToast('error', r.msg ?? 'Failed to generate trace')
  }

  const queued = processing.isqueuedtrace
  const running = processing.isrunningtrace
  return (
    <TaActionButton
      title={queued ? 'Queued' : 'Generate Trace'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={!hasAnswer || !!disabled}
      running={running}
      queued={queued}
      icon={icon ?? <BiShapeTriangle size={ICON_SM} />}
      iconSize={iconSize}
      sx={sx}
      testId={testId}
    />
  )
}
